/**
 * Parameter Discovery
 * ───────────────────
 * Reads every parameter from the workbook and normalises it into a
 * `ParameterDescriptor`, mapping Tableau data types + domain restrictions to the
 * appropriate UI control. This is the single source of truth for "what controls
 * do we render".
 */

import type {
  AllowableValue,
  ControlType,
  ParameterDescriptor,
  ParameterValue,
} from "@/types";

/** Convert a Tableau DataValue into a JSON-safe ParameterValue. */
export function normalizeValue(
  dataType: ParameterDescriptor["dataType"],
  dv: Tableau.DataValue | undefined,
): ParameterValue {
  if (!dv) return null;
  const raw = dv.nativeValue ?? dv.value;
  switch (dataType) {
    case "bool":
      return Boolean(raw);
    case "int":
    case "float":
      return raw === null || raw === undefined ? null : Number(raw);
    case "date":
    case "date-time":
      // Store dates as ISO strings for portability.
      if (raw instanceof Date) return raw.toISOString();
      return raw === null || raw === undefined ? null : String(raw);
    default:
      return raw === null || raw === undefined ? null : String(raw);
  }
}

/** Decide which control to render from data type + domain restriction. */
export function resolveControlType(
  dataType: ParameterDescriptor["dataType"],
  domain: "all" | "list" | "range",
  allowableCount: number,
): ControlType {
  if (dataType === "bool") return "boolean";
  if (domain === "list") {
    // A larger allowable list benefits from search; small lists use a plain
    // dropdown. "Dynamic" Tableau parameters also arrive as lists.
    return allowableCount > 8 ? "dynamic-list" : "list";
  }
  switch (dataType) {
    case "int":
    case "float":
      return "number";
    case "date":
      return "date";
    case "date-time":
      return "datetime";
    default:
      return "text";
  }
}

function mapDomain(p: Tableau.Parameter): "all" | "list" | "range" {
  const t = globalThis.tableau;
  const type = p.allowableValues?.type;
  if (type === t?.ParameterValueType.List) return "list";
  if (type === t?.ParameterValueType.Range) return "range";
  return "all";
}

export function describeParameter(p: Tableau.Parameter): ParameterDescriptor {
  const dataType = p.dataType as ParameterDescriptor["dataType"];
  const domain = mapDomain(p);

  const allowableValues: AllowableValue[] | undefined =
    domain === "list"
      ? (p.allowableValues.allowableValues ?? []).map((dv) => ({
          value: normalizeValue(dataType, dv),
          label: dv.formattedValue ?? String(dv.value),
        }))
      : undefined;

  const range =
    domain === "range"
      ? {
          min: normalizeValue(dataType, p.allowableValues.min) ?? undefined,
          max: normalizeValue(dataType, p.allowableValues.max) ?? undefined,
          stepSize: p.allowableValues.stepSize,
        }
      : undefined;

  return {
    id: p.id,
    name: p.name,
    dataType,
    domain,
    controlType: resolveControlType(dataType, domain, allowableValues?.length ?? 0),
    currentValue: normalizeValue(dataType, p.currentValue),
    allowableValues,
    range: range as ParameterDescriptor["range"],
    required: false, // set later from admin config
  };
}

/** Discover all parameters and return normalised descriptors.
 *
 * Parameters are global to the workbook but are read through the dashboard
 * object (`dashboardContent.dashboard.getParametersAsync()`), which is the
 * supported entry point for a dashboard extension. */
export async function discoverParameters(): Promise<ParameterDescriptor[]> {
  const dashboard = globalThis.tableau?.extensions?.dashboardContent?.dashboard;
  if (!dashboard) {
    throw new Error("Tableau dashboard unavailable — not running inside Tableau.");
  }
  const params = await dashboard.getParametersAsync();
  return params.map(describeParameter);
}
